import React, { useEffect, useRef } from "react";
import { Form, Formik, FormikProps } from "formik";
import { CheckboxField } from "../common/form-fields/CheckboxField";
import { yup } from "../../validation/yup";
import { TextareaField } from "../common/form-fields/TextareaField";
import { RequestState } from "../../constants/request-state";
import { isRequesting, isRequestSuccess } from "../../utils/request-state.utils";
import { useAutoFocus } from "../../effects/useAutoFocus";

const noteValidationSchema = yup.object().shape({
    text: yup.string().trim().required().max(255),
});

export interface NoteContent {
    isPrivate: boolean;
    text: string;
}

interface Props {
    isPrivate?: boolean;
    text?: string;
    saveButtonText?: string;
    requestState?: RequestState;
    renderCustomButtons?: () => React.ReactNode;
    onSave: (note: NoteContent) => void;
    onCancel: () => void;
}
export function EditNote({
    requestState = RequestState.none,
    saveButtonText = "add note",
    isPrivate = true,
    text = "",
    renderCustomButtons,
    onSave,
    onCancel,
}: Props) {
    const formikRef = useRef<FormikProps<NoteContent>>(null);
    const textareaRef = useAutoFocus<HTMLTextAreaElement>();

    useEffect(() => {
        if (isRequestSuccess(requestState) && formikRef.current) {
            formikRef.current.resetForm();
        }
    }, [requestState])

    return (
        <Formik
            innerRef={formikRef}
            initialValues={{ isPrivate, text }}
            validationSchema={noteValidationSchema}
            validateOnChange
            validateOnBlur={false}
            onSubmit={note => onSave(note)}
        >
            {({ values, isValid, dirty }) =>
                <Form noValidate={true} className="notes-create-item">
                    <CheckboxField
                        name="isPrivate"
                        label="Make Private"
                        wrapperClassName="notes-private-checkbox"
                        disabled={isRequesting(requestState)}
                    />
                    <div className="notes-private-info">Notes are private, or can be shared within your company.</div>
                    <TextareaField
                        textareaRef={textareaRef}
                        disabled={isRequesting(requestState)}
                        name="text"
                        placeholder="Add Notes"
                        wrapperClassName="notes-message"
                        maxLength={255}
                    />
                    <div className="notes-footer">
                        {renderCustomButtons?.()}
                        <div className="notes-action-buttons">
                            <button
                                type="button"
                                aria-label="cancel note edit"
                                className="btn btn-ghost"
                                disabled={isRequesting(requestState)}
                                onClick={onCancel}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                aria-label="save note"
                                className="btn btn-main"
                                disabled={!isValid || !values.text || !dirty || isRequesting(requestState)}
                            >
                                {saveButtonText}
                            </button>
                        </div>
                    </div>
                </Form>}
        </Formik>
    )
}